import { useState } from "react";
import { DateTimeDisplay, Breadcrumbs, StatusMessage } from "../../components/ui";
import Modal from "../../components/ui/Modal";
import { ModalContent } from "../../components/ui/ModalContent";
import type { CSHFormData } from "../../components/forms/CSHForm";
import { useAuthContext } from "../../contexts/useAuthContext";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { CshGroupEditor } from "../../features/csh/CshGroupEditor";
import { CshGroupList } from "../../features/csh/CshGroupList";
import { useCshGroups } from "../../features/csh/useCshGroups";
import type { CshGroup } from "../../services/csh/csh.types";

const CSHGroups = () => {
    const { isAdmin, isSupport } = useAuthContext();
    const canManage = isAdmin() && !isSupport();
    const data = useCshGroups(canManage);
    const breadcrumbs = useBreadcrumbs({ currentPageTitle: "CSH Groups" });
    const [editor, setEditor] = useState<{ group: CshGroup | null } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<CshGroup | null>(null);

    const openEditor = (group: CshGroup | null) => {
        if (!canManage) return;
        data.setError(null);
        data.setModalError(null);
        setEditor({ group });
    };
    const save = async (values: CSHFormData) => {
        if (!canManage) throw new Error("You do not have permission to manage CSH groups");
        if (await data.save(values, editor?.group ?? null)) {
            setEditor(null);
            globalThis.scrollTo(0, 0);
        }
    };
    const remove = async () => {
        if (!canManage) throw new Error("You do not have permission to manage CSH groups");
        const target = deleteTarget;
        setDeleteTarget(null);
        if (target) await data.remove(target.id);
        globalThis.scrollTo(0, 0);
    };

    return (
        <main>
            <div className="container mb-2">
                {editor && (
                    <CshGroupEditor group={editor.group} users={data.users} customers={data.customers}
                        submitting={data.submitting} error={data.modalError}
                        onClearError={() => data.setModalError(null)} onSave={save} onCancel={() => setEditor(null)} />
                )}
                <Modal isOpen={deleteTarget !== null} title="Delete CSH Group" headerClass="modal__header--danger"
                    content={<ModalContent heading="Delete CSH Group" message="Are you sure you want to delete this CSH group?" />}
                    onConfirm={remove} onCancel={() => setDeleteTarget(null)} />
                <section className="page">
                    <div className="heading__section">
                        <div className="heading__title-group">
                            <h2 className="heading__title heading__title--csh">CSH Groups</h2>
                            <DateTimeDisplay />
                            <Breadcrumbs items={breadcrumbs} />
                        </div>
                        <div>
                            {canManage && !editor && (
                                <button className="btn btn-add-new" disabled={data.loading || !data.lookupsReady}
                                    onClick={() => openEditor(null)}>Add CSH Group</button>
                            )}
                        </div>
                    </div>
                </section>
                {data.success && <StatusMessage key={data.success.message} kind="success" onClose={() => data.setSuccess(null)}>{data.success.message}</StatusMessage>}
                {data.error && <StatusMessage kind="error" onClose={() => data.setError(null)}>{data.error}</StatusMessage>}
                <section className="page">
                    <div className="alarm-content">
                        <CshGroupList groups={data.groups} loading={data.loading} canManage={canManage && data.lookupsReady}
                            downloadingSites={data.downloadingSites} downloadingUsers={data.downloadingUsers}
                            onDownload={(id, kind) => { void data.download(id, kind); }}
                            onEdit={openEditor} onDelete={setDeleteTarget} />
                    </div>
                </section>
            </div>
        </main>
    );
};

export default CSHGroups;
